/**
 * SectionSettingsEditor
 * Editor for section styling settings (background color, padding, etc.)
 */

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { SectionSettings, PaddingSize } from '@limo/shared'

interface SectionSettingsEditorProps {
  settings: SectionSettings
  onChange: (settings: SectionSettings) => void
}

// Preset color palette
const COLOR_PRESETS = [
  { name: 'White', value: '#ffffff' },
  { name: 'Light Gray', value: '#f9fafb' },
  { name: 'Gray', value: '#f3f4f6' },
  { name: 'Dark', value: '#1f2937' },
  { name: 'Slate', value: '#0f172a' },
  { name: 'Primary', value: '#1f7a99' },
  { name: 'Warm', value: '#fef3c7' },
  { name: 'Cool', value: '#e0f2fe' },
]

// Text color presets
const TEXT_COLOR_PRESETS = [
  { name: 'Dark', value: '#1f2937' },
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#ffffff' },
  { name: 'Gray', value: '#6b7280' },
  { name: 'Primary', value: '#1f7a99' },
]

// Padding size options
const PADDING_OPTIONS: { label: string; value: PaddingSize }[] = [
  { label: 'None', value: 'none' },
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
]

export function SectionSettingsEditor({
  settings,
  onChange,
}: SectionSettingsEditorProps) {
  const updateSetting = <K extends keyof SectionSettings>(
    key: K,
    value: SectionSettings[K]
  ) => {
    onChange({ ...settings, [key]: value })
  }

  return (
    <div className="space-y-6">
      {/* Background Color */}
      <div className="space-y-3">
        <Label>Background Color</Label>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => updateSetting('backgroundColor', preset.value)}
                className={cn(
                  'w-8 h-8 rounded-md border-2 transition-all',
                  settings.backgroundColor === preset.value
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-gray-200 hover:border-gray-300'
                )}
                style={{ backgroundColor: preset.value }}
                title={preset.name}
              />
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <Input
              type="text"
              value={settings.backgroundColor || ''}
              onChange={(e) => updateSetting('backgroundColor', e.target.value)}
              placeholder="#ffffff or rgb(255,255,255)"
              className="flex-1"
            />
            <input
              type="color"
              value={settings.backgroundColor || '#ffffff'}
              onChange={(e) => updateSetting('backgroundColor', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border border-gray-200"
            />
          </div>
        </div>
      </div>

      {/* Text Color */}
      <div className="space-y-3">
        <Label>Text Color</Label>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {TEXT_COLOR_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => updateSetting('textColor', preset.value)}
                className={cn(
                  'w-8 h-8 rounded-md border-2 transition-all',
                  settings.textColor === preset.value
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-gray-200 hover:border-gray-300'
                )}
                style={{ backgroundColor: preset.value }}
                title={preset.name}
              />
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <Input
              type="text"
              value={settings.textColor || ''}
              onChange={(e) => updateSetting('textColor', e.target.value)}
              placeholder="#1f2937 or rgb(31,41,55)"
              className="flex-1"
            />
            <input
              type="color"
              value={settings.textColor || '#1f2937'}
              onChange={(e) => updateSetting('textColor', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border border-gray-200"
            />
          </div>
        </div>
      </div>

      {/* Padding */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="padding-top">Padding Top</Label>
          <Select
            value={settings.paddingTop || 'medium'}
            onValueChange={(value) =>
              updateSetting('paddingTop', value as PaddingSize)
            }
          >
            <SelectTrigger id="padding-top">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PADDING_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="padding-bottom">Padding Bottom</Label>
          <Select
            value={settings.paddingBottom || 'medium'}
            onValueChange={(value) =>
              updateSetting('paddingBottom', value as PaddingSize)
            }
          >
            <SelectTrigger id="padding-bottom">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PADDING_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Full Width */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="full-width">Full Width</Label>
          <p className="text-xs text-muted-foreground">
            Extend section to full browser width
          </p>
        </div>
        <Switch
          id="full-width"
          checked={settings.fullWidth || false}
          onCheckedChange={(checked) => updateSetting('fullWidth', checked)}
        />
      </div>

      {/* Custom CSS Classes (Advanced) */}
      <div className="space-y-2">
        <Label htmlFor="custom-classes">Custom CSS Classes</Label>
        <Input
          id="custom-classes"
          value={settings.customClasses || ''}
          onChange={(e) => updateSetting('customClasses', e.target.value)}
          placeholder="e.g., my-custom-class another-class"
        />
        <p className="text-xs text-muted-foreground">
          Advanced: Add custom Tailwind or CSS classes
        </p>
      </div>
    </div>
  )
}
